import * as React from "react";
import { ArrowUp } from "lucide-react";

import { Button } from "@/components/ui/button";
import { CircleLoader } from "@/components/ui/circleLoader";
import { Textarea } from "@/components/ui/textarea";
import type { InstructionsRunReport } from "../documentAuthoring.types";
import { RunReport } from "./RunReport";

interface Props {
  aiAuthor: string;
  busy: boolean;
  running: boolean;
  editorReady: boolean;
  error: string | null;
  report: InstructionsRunReport | null;
  onRun: (instructions: string) => void;
  onCancel: () => void;
}

const QUICK_PROMPTS = [
  {
    label: "Review as the recipient",
    text: "We are the receiving party. Review this agreement and redline any terms that are one-sided against us, proposing balanced, market-standard wording.",
  },
  { label: "English law", text: "Change the governing law and jurisdiction to England and Wales." },
  {
    label: "Cap liability",
    text: "Make sure any indemnity or liability is capped. Propose a reasonable cap and exclude indirect and consequential losses.",
  },
  {
    label: "Tidy drafting",
    text: "Fix typos, grammar, inconsistent defined terms and broken cross-references. Do not change the commercial meaning.",
  },
];

export const InstructionsTab: React.FC<Props> = ({
  aiAuthor,
  busy,
  running,
  editorReady,
  error,
  report,
  onRun,
  onCancel,
}) => {
  const [instructions, setInstructions] = React.useState("");
  const [elapsed, setElapsed] = React.useState(0);
  const canRun = editorReady && !busy && instructions.trim().length > 0;

  React.useEffect(() => {
    if (!running) return;
    setElapsed(0);
    const started = Date.now();
    const id = window.setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [running]);

  const run = () => {
    if (canRun) onRun(instructions.trim());
  };

  return (
    <div className="flex flex-col">
      <section className="space-y-3 px-4 py-3">
        {error && (
          <p className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-xs text-danger" role="alert">
            {error}
          </p>
        )}
        <label
          htmlFor="authoring-instructions"
          className="text-xxs font-semibold uppercase tracking-wide text-gray-500"
        >
          What should {aiAuthor} change?
        </label>
        <div className="relative">
          <Textarea
            id="authoring-instructions"
            value={instructions}
            onChange={event => setInstructions(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) run();
            }}
            placeholder="For example: we're the supplier. Limit our liability and remove the unlimited indemnity."
            rows={5}
            disabled={busy}
            className="pr-12"
          />
          <Button
            type="button"
            size="icon"
            variant="coral"
            className="absolute bottom-2 right-2 h-8 w-8"
            onClick={run}
            disabled={!canRun}
            aria-label="Run review"
            title="Run review (Ctrl+Enter)"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        </div>
        <p className="flex items-center gap-2 text-xs text-gray-500">
          {running ? (
            <>
              <CircleLoader fill="#6b7280" /> Reviewing… {elapsed}s
              <Button type="button" variant="link" size="xs" onClick={onCancel}>
                Cancel
              </Button>
            </>
          ) : (
            "Press the arrow or Ctrl+Enter to run the review."
          )}
        </p>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Example instructions">
          {QUICK_PROMPTS.map(prompt => (
            <Button
              key={prompt.label}
              type="button"
              variant="light-beige-secondary"
              size="xs"
              className="rounded-full text-xxs"
              onClick={() => setInstructions(prompt.text)}
              disabled={busy}
            >
              {prompt.label}
            </Button>
          ))}
        </div>
      </section>
      {report && <RunReport report={report} aiAuthor={aiAuthor} />}
    </div>
  );
};
